import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import get from 'lodash.get';
import { connect } from 'react-redux';
import produce from 'immer';

import CustomPrintCheckout from '../../consumer/components/custom-prints/checkout/CustomPrintCheckout';
import useCustomPrintCheckoutState, {
  CustomPrintCheckoutStateProvider,
} from '../../consumer/hooks/useCustomPrintCheckoutState';
import { openModal } from '../../../actions/modal-actions';
import {
  WebGalleryAlbumCustomPrintForCheckoutFromAlbum,
  WebGalleryCustomPrintConfigurationPhotos,
} from '../graphql/queries/custom-prints';
import { AccountForWebGalleries } from '../graphql/queries/account';
import { checkoutPrice } from '../../../graphql/queries/checkout';
import { checkout } from '../../../graphql/mutations/checkout';
import { accountAddressCreate } from '../../../graphql/mutations/account';
import { ANALYTIC_EVENTS, trackWebGalleryEvent } from '../utilities/analytics';
import { createCustomPrintCandidatePhotoGroups } from '../../../components/global/CustomPrintConfigurator/helpers';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      albumId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

const WebGalleriesCustomPrintCheckout = ({ history, match, ...props }) => {
  return (
    <CustomPrintCheckout
      onComplete={() => {
        history.push(`/galleries/view/album/${match.params.albumId}`);
      }}
      onCheckoutSuccess={albumCustomPrint => {
        trackWebGalleryEvent(ANALYTIC_EVENTS.COLLAGE_CHECKOUT_SUCCESS, {
          custom_print_id: get(albumCustomPrint, 'id', ''),
          album_id: get(albumCustomPrint, 'album.id', ''),
        });
      }}
      {...props}
    />
  );
};

WebGalleriesCustomPrintCheckout.propTypes = propTypes;

const withAlbumCustomPrintFromAlbum = graphql(
  WebGalleryAlbumCustomPrintForCheckoutFromAlbum,
  {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumId,
      },
    }),
    props: ({ data: { album, loading } }) => ({
      loading,
      albumCustomPrint: get(album, 'custom_prints.records', [])[0],
    }),
  },
);

const withCustomPrintCheckoutStateProvider = WrappedComponent => props => {
  return (
    <CustomPrintCheckoutStateProvider>
      <WrappedComponent {...props} />
    </CustomPrintCheckoutStateProvider>
  );
};

const withCustomPrintCheckoutState = WrappedComponent => props => {
  const {
    state: { activeIdentityId },
  } = useCustomPrintCheckoutState();

  return <WrappedComponent {...props} activeIdentityId={activeIdentityId} />;
};

const withUserAccountAndIdentities = graphql(AccountForWebGalleries, {
  props: ({ data: { me, loading } }) => ({
    account: me,
    loadingAccount: loading,
  }),
});

const withPhotosForConfig = graphql(WebGalleryCustomPrintConfigurationPhotos, {
  options: ownProps => ({
    variables: {
      albumId: ownProps.match.params.albumId,
    },
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { album, fetchMore } }) => ({
    candidatePhotoGroups: createCustomPrintCandidatePhotoGroups({
      source: album,
      mappings: [
        {
          id: 'albumPhotos',
          label: 'All Photos',
          path: 'photos',
        },
        {
          id: 'favoritePhotos',
          label: 'My Favorites',
          path: 'root_album_folder.account_favorite_photos',
          mapper: record => record.photo,
        },
      ],
      fetchMore,
    }),
  }),
});

const withCheckoutPrice = graphql(checkoutPrice, {
  options: ({ account, albumCustomPrint, activeIdentityId }) => ({
    variables: {
      account_id: account.id,
      custom_print_items: [
        {
          album_custom_print_id: albumCustomPrint.id,
          identity_id: activeIdentityId,
          quantity: 1,
          include_shipping: true,
        },
      ],
    },
  }),
  props: ({ data: { checkoutPrice: response } }) => ({
    price: response,
  }),
  skip: ({ account, activeIdentityId, albumCustomPrint }) =>
    !account || !activeIdentityId || !albumCustomPrint,
});

const withCheckout = graphql(checkout, {
  props: ({ mutate }) => ({
    checkout: ({
      accountId,
      accountAddressId,
      email,
      stripeTokenId,
      customPrintItems,
      stripePaymentMethodId,
      stripeConnectAccountId,
    }) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            email_address: email,
            account_address_id: accountAddressId,
            stripe_token: stripeTokenId,
            stripe_payment_method_id: stripePaymentMethodId,
            stripe_connect_account_id: stripeConnectAccountId,
            custom_print_items: customPrintItems,
            platform: 'WEB',
          },
        },
      }),
  }),
});

const withAccountAddressCreate = graphql(accountAddressCreate, {
  props: ({ mutate }) => ({
    accountAddressCreate: (userId, addressData) =>
      mutate({
        variables: {
          input: {
            ...addressData,
            account_id: userId,
          },
        },
        update: (store, { data: { accountAddressCreate: response } }) => {
          const data = store.readQuery({
            query: AccountForWebGalleries,
          });

          const address = response.account_address;
          const updatedData = produce(data, draft => {
            draft.me.addresses.unshift(address);
          });

          store.writeQuery({
            query: AccountForWebGalleries,
            data: updatedData,
          });
        },
      }),
  }),
});

const mapDispatchToProps = {
  openModal,
};

export default compose(
  withRouter,
  withCustomPrintCheckoutStateProvider,
  withCustomPrintCheckoutState,
  withAlbumCustomPrintFromAlbum,
  withUserAccountAndIdentities,
  withCheckoutPrice,
  withPhotosForConfig,
  withCheckout,
  withAccountAddressCreate,
  connect(null, mapDispatchToProps),
)(WebGalleriesCustomPrintCheckout);
